import axios from "axios";
import { API_BASE_URL } from "../config/api";
import {
  isCompanyWorkspaceUser,
  mergeUserWithStaffSession,
  resolveStaffDetailsForBooking,
} from "../utils/companyStaffSession";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Rooms API
export const roomsAPI = {
  getRooms: (params) => api.get("/api/meetings/rooms", { params }),
  getRoom: (id) => api.get(`/api/meetings/rooms/${id}`),
  getRoomQA: (id) => api.get(`/api/meetings/rooms/${id}/qa`),
  submitRoomQuestion: (id, payload) => api.post(`/api/meetings/rooms/${id}/qa`, payload),
  syncRoom: (roomData) => api.post("/api/meetings/rooms/sync", roomData),
  createRoom: (roomData) => api.post("/api/meetings/rooms", roomData),
  updateRoom: (id, roomData) => api.put(`/api/meetings/rooms/${id}`, roomData),
  deleteRoom: (id) => {
    if (!id) {
      return Promise.reject(new Error("Room ID is required"));
    }
    return api.delete(`/api/meetings/rooms/${id}`);
  },
  getLocationSuggestions: (query) => api.get("/api/meetings/rooms/locations/autocomplete", { params: { q: query } }),
};

// Bookings API
export const bookingsAPI = {
  getMyBookings: () => api.get("/api/meetings/bookings/my-bookings"),
  getAllBookings: () => api.get("/api/meetings/bookings/all"),
  getRoomBookings: (roomId, date) => {
    if (!roomId || !date) {
      console.error("❌ getRoomBookings: Missing roomId or date", { roomId, date });
      return Promise.reject(new Error("Room ID and date are required"));
    }
    return api.get(`/api/meetings/bookings/room/${roomId}?date=${date}`);
  },
  createBooking: (bookingData) => {
    // ✅ Validate booking data before API call
    if (!bookingData) {
      console.error("❌ createBooking: bookingData is undefined or null");
      return Promise.reject(new Error("Booking data is required"));
    }

    if (!bookingData.room) {
      console.error("❌ createBooking: room is missing", bookingData);
      return Promise.reject(new Error("Room ID is required"));
    }

    if (!bookingData.startTime) {
      console.error("❌ createBooking: startTime is missing", bookingData);
      return Promise.reject(new Error("Start time is required"));
    }

    if (!bookingData.endTime) {
      console.error("❌ createBooking: endTime is missing", bookingData);
      return Promise.reject(new Error("End time is required"));
    }

    if (!bookingData.purpose) {
      console.error("❌ createBooking: purpose is missing", bookingData);
      return Promise.reject(new Error("Purpose is required"));
    }

    if (!bookingData.team) {
      console.error("❌ createBooking: team is missing", bookingData);
      return Promise.reject(new Error("Team is required"));
    }

    let sessionUser = null;
    try {
      const sessionUserRaw = localStorage.getItem("user");
      sessionUser = sessionUserRaw ? mergeUserWithStaffSession(JSON.parse(sessionUserRaw)) : null;
    } catch {
      sessionUser = null;
    }
    const isCompanyAccount = isCompanyWorkspaceUser(sessionUser);
    const staffOverrides = bookingData.staffDetails || null;
    const { staffDetails: _ignored, ...bookingPayload } = bookingData;
    const staffDetails = isCompanyAccount
      ? resolveStaffDetailsForBooking(sessionUser, staffOverrides || {})
      : null;

    if (isCompanyAccount && !staffDetails) {
      return Promise.reject(
        new Error("Please complete your name and role in your account before booking.")
      );
    }

    const payload = staffDetails ? { ...bookingPayload, staffDetails } : bookingPayload;
    console.log("✅ createBooking: All validations passed, sending request");
    return api.post("/api/meetings/bookings", payload, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  },
  updateBooking: (id, bookingData) => api.put(`/api/meetings/bookings/${id}`, bookingData),
  cancelBooking: (id, cancelData) =>
    api.patch(`/api/meetings/bookings/${id}/cancel`, cancelData),
  deleteBooking: (id) => api.delete(`/api/meetings/bookings/${id}`),
  getBookingsByDate: (date, includeCanceled) =>
    api.get(`/api/meetings/bookings/by-date/${date}${includeCanceled ? '?includeCanceled=true' : ''}`),
};

// Teams API
export const teamsAPI = {
  getAllTeams: () => api.get("/api/meetings/teams"), // Public endpoint
  getMyTeams: () => api.get("/api/meetings/teams/my-teams"), // Requires auth - only user's teams
  createTeam: (teamData) => api.post("/api/meetings/teams", teamData), // Create team
  updateTeam: (id, teamData) => api.put(`/api/meetings/teams/${id}`, teamData), // Update team
  deleteTeam: (id) => api.delete(`/api/meetings/teams/${id}`), // Delete team
  checkMemberAvailability: (members, startTime, endTime) =>
    api.post("/api/meetings/teams/check-availability", { members, startTime, endTime }),
};

// Office Meeting Room API
export const officeSpaceAPI = {
  getMyOfficeSpace: () => api.get("/api/meetings/office-space/my-office-space"),
  createOrUpdateOfficeSpace: (officeSpaceData) => api.post("/api/meetings/office-space/my-office-space", officeSpaceData),
  updateOfficeSpace: (officeSpaceData) => api.put("/api/meetings/office-space/my-office-space", officeSpaceData),
};

// Company Profile API (company_admin only)
export const companyProfileAPI = {
  getMyCompanyProfile: () => api.get("/api/company/profile"),
  createCompanyProfile: (profileData) => api.post("/api/company/profile", profileData),
  updateCompanyProfile: (profileData) => api.patch("/api/company/profile", profileData),
};

export default api;

